import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../contexts/AuthContext';
import { FaUpload, FaTimes, FaImage, FaFileAlt, FaPaperPlane, FaFilter, FaCheckCircle, FaBell, FaWhatsapp, FaFileInvoiceDollar, FaSync } from 'react-icons/fa';
import Notification from '../common/Notification';
import SendProgressModal from '../common/SendProgressModal';
import WhatsAppPreviewModal from '../common/WhatsAppPreviewModal';
import '../../styles/whatsapp/BulkNotice.css';

const SEND_METHODS = [
  { value: 'both', label: 'Notification + WhatsApp', icon: '🔔+💬' },
  { value: 'whatsapp_only', label: 'WhatsApp Only', icon: '💬' },
  { value: 'notification_only', label: 'Notification Only', icon: '🔔' },
];

const BulkNotice = () => {
  const { user } = useAuth();
  const location = useLocation();
  // API Base URL - use environment variable or fallback to deployed backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

  // Template fields instead of single message
  const [noticeDate, setNoticeDate] = useState(new Date().toISOString().split('T')[0]); // Today's date in YYYY-MM-DD format
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeBody, setNoticeBody] = useState('');
  
  const [sendMethod, setSendMethod] = useState('both');
  const [attachments, setAttachments] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [sending, setSending] = useState(false);
  
  // Filter states
  const [classFilter, setClassFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStatus, setProgressStatus] = useState('sending'); // 'sending', 'completed', 'error'
  const [totalMessages, setTotalMessages] = useState(0);
  const [sentMessages, setSentMessages] = useState(0);
  const [failedMessages, setFailedMessages] = useState(0);
  const [skippedMessages, setSkippedMessages] = useState(0);
  const [currentRecipient, setCurrentRecipient] = useState(null);
  const [sendResults, setSendResults] = useState({ successful: [], failed: [], skipped: [] });

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Image preview modal
  const [previewImage, setPreviewImage] = useState(null);

  // ===== DEMAND SLIP MODE =====
  const [pageMode, setPageMode] = useState('notice'); // 'notice' | 'demand_slip'
  const [demandSlipData, setDemandSlipData] = useState([]); // computed fee data per student
  const [loadingDemandSlip, setLoadingDemandSlip] = useState(false);
  const [demandSlipDate, setDemandSlipDate] = useState(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }));
  const [demandSlipMonth, setDemandSlipMonth] = useState(new Date().toLocaleString('en-IN', { month: 'long' }));
  const [showDemandSlipPreview, setShowDemandSlipPreview] = useState(false);
  const [demandSlipSort, setDemandSlipSort] = useState({ field: null, direction: 'asc' });
  const [forceSend, setForceSend] = useState(false);
  const EXAM_FEE_MONTHS = [3, 7, 11]; // March, July, November

  const showNotification = useCallback((msg, type = 'success') => {
    setNotification({ show: true, message: msg, type });
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch main student data
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .eq('Active', true)
        .order('Class', { ascending: true })
        .order('Name', { ascending: true });

      if (studentsError) throw studentsError;
      
      // Fetch private details
      const { data: privateData, error: privateError } = await supabase
        .from('students_private_details')
        .select('*');
        
      if (privateError) throw privateError;
      
      // Create a map for quick lookup of private data
      const privateDataMap = {};
      privateData.forEach(item => {
        privateDataMap[item.Rno] = item;
      });
      
      // Merge data from both tables
      const mergedData = studentsData.map(student => {
        const privateDetails = privateDataMap[student.Rno] || {};
        return {
          ...student,
          Mobile1: privateDetails.Mobile1 || null,
          Mobile2: privateDetails.Mobile2 || null,
          private_details: privateDetails
        };
      });
      
      setStudents(mergedData || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      showNotification('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Load gallery images passed from Gallery page
  useEffect(() => {
    if (location.state?.galleryImages?.length > 0) {
      const galAttachments = location.state.galleryImages.map(img => ({
        file: null,
        name: img.url.split('/').pop().split('?')[0] || `gallery_${Date.now()}.jpg`,
        size: 0,
        type: 'image/jpeg',
        preview: img.url,
        isGallery: true,
        cloudinaryUrl: img.url,
      }));
      setAttachments(galAttachments);
      // Clear location state so refreshing doesn't re-add
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Extract unique classes from students
    const classes = [...new Set(students.map(s => s.Class).filter(Boolean))].sort();
    setAvailableClasses(classes);
  }, [students]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke URL for memory cleanup (skip gallery images — they use Cloudinary URLs)
      if (prev[index].preview && !prev[index].isGallery) {
        URL.revokeObjectURL(prev[index].preview);
      }
      return updated;
    });
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <FaImage className="file-icon image" />;
    return <FaFileAlt className="file-icon document" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredStudents = students.filter(student => {
    // Apply class filter
    if (classFilter !== 'all' && student.Class !== classFilter) return false;
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        (student.Name?.toLowerCase() || '').includes(search) ||
        (student.Rno?.toString() || '').includes(search) ||
        (student.Class?.toLowerCase() || '').includes(search) ||
        (String(student.Mobile1 || '')).includes(search) ||
        (String(student.Mobile2 || '')).includes(search)
      );
    }
    
    return true;
  });

  const toggleStudentSelection = (rno) => {
    setSelectedStudents(prev =>
      prev.includes(rno)
        ? prev.filter(id => id !== rno)
        : [...prev, rno]
    );
  };

  const selectAllFiltered = () => {
    const allFilteredRnos = filteredStudents.map(s => s.Rno);
    setSelectedStudents(allFilteredRnos);
  };

  const clearSelection = () => {
    setSelectedStudents([]);
  };

  // Save announcement record to Supabase
  // Smart recipient_rnos (stored as text[] in database):
  //   - All students selected → ["all"]
  //   - Entire class selected (via filter) → ["class:ClassName"]
  //   - Specific students → ["101", "102", "103", ...] (strings)
  const getSmartRecipientRnos = () => {
    // Check if ALL students are selected
    if (selectedStudents.length === students.length) {
      return ['all'];
    }

    // Check if a class filter is active and ALL students in that class are selected
    if (classFilter !== 'all') {
      const classStudentRnos = students
        .filter(s => s.Class === classFilter)
        .map(s => s.Rno);
      const allClassSelected = classStudentRnos.length > 0 &&
        classStudentRnos.every(rno => selectedStudents.includes(rno)) &&
        selectedStudents.length === classStudentRnos.length;
      if (allClassSelected) {
        return [`class:${classFilter}`];
      }
    }

    // Otherwise save individual rnos as strings
    return selectedStudents.map(rno => String(rno));
  };

  const saveAnnouncementRecord = async (successCount, failedCount, mediaIds = []) => {
    try {
      const record = {
        created_by: user?.id || null,
        title: noticeTitle,
        body: noticeBody,
        notice_date: noticeDate,
        media_ids: mediaIds.length > 0 ? mediaIds.map(m => m.id) : null,
        image_urls: mediaIds.length > 0 ? mediaIds.map(m => m.cloudinaryUrl).filter(Boolean) : null,
        media_type: attachments.length > 0 ? (attachments[0].type.startsWith('image/') ? 'image' : 'document') : null,
        send_method: sendMethod,
        recipient_count: selectedStudents.length,
        success_count: successCount,
        failed_count: failedCount,
        recipient_rnos: getSmartRecipientRnos(),
      };
      console.log('📝 Saving announcement record:', JSON.stringify(record));
      const { data, error } = await supabase.from('announcements').insert(record).select();
      if (error) {
        console.error('❌ Failed to save announcement record:', error);
        showNotification('Failed to save announcement record: ' + error.message, 'error');
      } else {
        console.log('✅ Announcement record saved:', data);
      }
    } catch (err) {
      console.error('❌ Error saving announcement:', err);
    }
  };

  // Show preview modal when send button is clicked
  const handleSendNotice = () => {
    const hasAttachments = attachments.length > 0;

    if (!noticeDate.trim() || !noticeTitle.trim()) {
      showNotification('Please fill in Date and Notice Title', 'error');
      return;
    }

    if (!hasAttachments && !noticeBody.trim()) {
      showNotification('Main Content is required for text-only notices', 'error');
      return;
    }

    if (selectedStudents.length === 0) {
      showNotification('Please select at least one student', 'error');
      return;
    }

    // Show preview modal
    setShowPreviewModal(true);
  };

  // Actual sending logic after preview confirmation
  const handleConfirmSend = async () => {
    // Close preview modal
    setShowPreviewModal(false);

    const shouldSendWhatsApp = sendMethod === 'both' || sendMethod === 'whatsapp_only';
    const shouldSendNotification = sendMethod === 'both' || sendMethod === 'notification_only';

    // ============================================
    // NOTIFICATION ONLY — send push to all selected students (BULK)
    // ============================================
    if (sendMethod === 'notification_only') {
      setSending(true);
      setTotalMessages(selectedStudents.length);
      setSentMessages(0);
      setFailedMessages(0);
      setSendResults({ successful: [], failed: [] });
      setProgressStatus('sending');
      setShowProgressModal(true);

      // Save announcement record FIRST so users can see it immediately on click
      setCurrentRecipient({ name: 'Saving announcement...', phone: '' });
      await saveAnnouncementRecord(selectedStudents.length, 0, []);

      // Send bulk push notification (single API call)
      setCurrentRecipient({ name: `Sending to ${selectedStudents.length} students...`, phone: '' });
      let successCount = 0;
      let failCount = 0;

      try {
        const res = await authFetch(`${API_BASE_URL}/api/push-token/bulk-send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: noticeTitle,
            body: noticeBody,
            studentRnos: selectedStudents,
          }),
        });
        const result = await res.json();
        if (result.success) {
          successCount = result.sent || selectedStudents.length;
          failCount = result.failed || 0;
        } else {
          failCount = selectedStudents.length;
        }
      } catch {
        failCount = selectedStudents.length;
      }

      setSentMessages(successCount);
      setFailedMessages(failCount);
      setProgressStatus('completed');
      setCurrentRecipient(null);

      if (successCount > 0) {
        setTimeout(() => {
          setNoticeDate(new Date().toISOString().split('T')[0]);
          setNoticeTitle('');
          setNoticeBody('');
          setAttachments([]);
          setSelectedStudents([]);
        }, 1000);
      }
      setSending(false);
      return;
    }

    // ============================================
    // WHATSAPP (or BOTH) — existing WhatsApp send flow
    // ============================================

    // Determine template based on file type
    const hasImages = attachments.some(att => att.type.startsWith('image/'));
    const hasDocuments = attachments.some(att => 
      att.type === 'application/pdf' ||
      att.type === 'application/msword' ||
      att.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      att.type === 'application/vnd.ms-excel' ||
      att.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    let templateName = '';
    if (hasImages && !hasDocuments) {
      templateName = 'notice_png_v2';
    } else if (hasDocuments && !hasImages) {
      templateName = 'notice_pdf_v1';
    } else if (hasImages && hasDocuments) {
      // If both types, prioritize documents
      templateName = 'notice_pdf_v1';
      showNotification('Mixed file types detected. Using PDF template.', 'info');
    } else {
      templateName = 'notice_text_v2';
    }

    // Get selected students' data
    const recipients = students
      .filter(s => selectedStudents.includes(s.Rno))
      .map(s => ({
        rno: s.Rno,
        name: s.Name,
        phone: s.Mobile1 ? `91${s.Mobile1}` : null,
        alternatePhone: s.Mobile2 ? `91${s.Mobile2}` : null
      }))
      .filter(s => s.phone);

    if (recipients.length === 0) {
      showNotification('Selected students have no phone numbers', 'error');
      return;
    }

    // ============================================
    // STEP 1: DEDUPLICATE PHONE NUMBERS
    // ============================================
    console.log('📊 Deduplicating phone numbers...');
    
    const phoneMap = new Map(); // phone -> [{rno, name}]
    const uniquePhones = new Set();
    
    recipients.forEach(recipient => {
      const phones = [];
      if (recipient.phone) phones.push(recipient.phone);
      if (recipient.alternatePhone) phones.push(recipient.alternatePhone);
      
      phones.forEach(phone => {
        uniquePhones.add(phone);
        if (!phoneMap.has(phone)) {
          phoneMap.set(phone, []);
        }
        phoneMap.get(phone).push({ rno: recipient.rno, name: recipient.name });
      });
    });

    const uniquePhoneList = Array.from(uniquePhones);
    const totalBeforeDedup = recipients.length * 2; // Assume max 2 phones each
    const duplicatesAvoided = totalBeforeDedup - uniquePhoneList.length;
    
    console.log(`✅ Deduplication complete:`);
    console.log(`   Selected students: ${recipients.length}`);
    console.log(`   Unique phone numbers: ${uniquePhoneList.length}`);
    console.log(`   Duplicate sends avoided: ${duplicatesAvoided}`);
    
    // Show deduplication info to user
    showNotification(
      `Sending to ${uniquePhoneList.length} unique numbers (${duplicatesAvoided} duplicates avoided)`,
      'info'
    );

    // ============================================
    // STEP 2: INITIALIZE PROGRESS MODAL
    // ============================================
    setTotalMessages(uniquePhoneList.length);
    setSentMessages(0);
    setFailedMessages(0);
    setSendResults({ successful: [], failed: [] });
    setProgressStatus('sending');
    setShowProgressModal(true);
    setSending(true);

    console.log('Sending notice...');
    console.log('Template:', templateName);
    console.log('Unique phone numbers:', uniquePhoneList.length);

    try {
      // Local counters for cumulative tracking
      let totalSuccessCount = 0;
      let totalFailureCount = 0;
      const allSuccessResults = [];
      const allFailedResults = [];

      // ============================================
      // STEP 3: UPLOAD MEDIA FILES ONCE
      // ============================================
      let uploadedMediaIds = [];
      if (attachments.length > 0) {
        try {
          const galleryAtts = attachments.filter(a => a.isGallery);
          const localAtts = attachments.filter(a => !a.isGallery);

          // Gallery images: already on Cloudinary, only upload to WhatsApp
          if (galleryAtts.length > 0) {
            console.log('📤 Uploading gallery images to WhatsApp...');
            setCurrentRecipient({ name: 'Uploading gallery images to WhatsApp...', phone: '' });
            const urls = galleryAtts.map(a => a.cloudinaryUrl);
            const urlRes = await authFetch(`${API_BASE_URL}/api/whatsapp/upload-media-from-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ urls }),
            });
            if (urlRes.ok) {
              const urlResult = await urlRes.json();
              if (urlResult.success && urlResult.mediaIds) {
                uploadedMediaIds.push(...urlResult.mediaIds);
                console.log(`✅ Gallery: ${urlResult.mediaIds.length} uploaded to WhatsApp (Cloudinary URLs preserved)`);
              }
            } else {
              throw new Error('Failed to upload gallery images to WhatsApp');
            }
          }

          // Local attachments: upload to both WhatsApp + Cloudinary
          if (localAtts.length > 0) {
            console.log('📤 Uploading local media files...');
            setCurrentRecipient({ name: 'Uploading media files...', phone: '' });
            const mediaFormData = new FormData();
            localAtts.forEach((attachment) => {
              mediaFormData.append('files', attachment.file);
            });
            const uploadResponse = await authFetch(`${API_BASE_URL}/api/whatsapp/upload-media`, {
              method: 'POST',
              body: mediaFormData,
            });
            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              if (uploadResult.success && uploadResult.mediaIds) {
                uploadedMediaIds.push(...uploadResult.mediaIds);
                console.log(`✅ Local: ${uploadResult.mediaIds.length} media file(s) uploaded`);
              }
            } else {
              throw new Error('Failed to upload media files');
            }
          }
        } catch (error) {
          console.error('❌ Media upload failed:', error);
          setProgressStatus('completed');
          showNotification('Failed to upload media files', 'error');
          setSending(false);
          return;
        }
      }

      // ============================================
      // STEP 4: SEND IN BATCHES OF 5
      // ============================================
      const BATCH_SIZE = 50;
      const totalBatches = Math.ceil(uniquePhoneList.length / BATCH_SIZE);
      
      console.log(`📦 Sending in ${totalBatches} batches of ${BATCH_SIZE}`);
      
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * BATCH_SIZE;
        const endIdx = Math.min(startIdx + BATCH_SIZE, uniquePhoneList.length);
        const batchPhones = uniquePhoneList.slice(startIdx, endIdx);
        
        const batchNum = batchIndex + 1;
        console.log(`\n📦 Processing Batch ${batchNum}/${totalBatches} (${batchPhones.length} numbers)`);
        
        // Update current recipient to show batch info
        setCurrentRecipient({
          name: `Batch ${batchNum}/${totalBatches}`,
          phone: `${batchPhones.length} numbers`
        });

        // Prepare recipients for this batch
        const batchRecipients = batchPhones.map(phone => {
          const students = phoneMap.get(phone);
          return {
            rno: students[0].rno,
            name: students.map(s => s.name).join(', '),
            phone: phone,
            alternatePhone: null
          };
        });

        try {
          // Prepare request body
          const requestBody = {
            templateName: templateName,
            templateParams: JSON.stringify({
              date: noticeDate || '.',
              notice_title: noticeTitle || '.',
              body: noticeBody || '.'
            }),
            recipients: JSON.stringify(batchRecipients),
            mediaIds: JSON.stringify(uploadedMediaIds)
          };

          console.log(`➡️ Sending batch ${batchNum} to backend...`);
          
          const response = await authFetch(`${API_BASE_URL}/api/whatsapp/send-notice`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
          
          console.log(`⬅️ Batch ${batchNum} response: ${response.status}`, response.ok ? '✅' : '❌');

          if (response.ok) {
            const result = await response.json();
            
            // Accumulate results from this batch
            if (result.success && result.details) {
              const batchSuccess = result.details.successCount || 0;
              const batchFailed = result.details.failedCount || 0;
              
              totalSuccessCount += batchSuccess;
              totalFailureCount += batchFailed;
              
              // Add successful results
              if (result.details.results && result.details.results.successful) {
                allSuccessResults.push(...result.details.results.successful);
              }
              
              // Add failed results
              if (result.details.results && result.details.results.failed) {
                allFailedResults.push(...result.details.results.failed);
              }
              
              // Update UI with cumulative counts
              setSentMessages(totalSuccessCount);
              setFailedMessages(totalFailureCount);
              setSendResults({
                successful: allSuccessResults,
                failed: allFailedResults
              });
              
              console.log(`✅ Batch ${batchNum}: ${batchSuccess} success, ${batchFailed} failed`);
              console.log(`   Total so far: ${totalSuccessCount}/${uniquePhoneList.length} sent, ${totalFailureCount} failed`);
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || errorData.message || 'HTTP request failed');
          }
        } catch (error) {
          // Handle batch send failure
          console.error(`❌ Batch ${batchNum} failed:`, error);
          
          // Count all phones in failed batch as failed
          batchPhones.forEach(phone => {
            const students = phoneMap.get(phone);
            totalFailureCount++;
            allFailedResults.push({
              phone: phone,
              students: students.map(s => s.name),
              error: error.message || 'Unknown error'
            });
          });
          
          setFailedMessages(totalFailureCount);
          setSendResults(prev => ({
            ...prev,
            failed: allFailedResults
          }));
        }

        // Small delay between batches
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // ============================================
      // STEP 5: SAVE ANNOUNCEMENT RECORD FIRST
      // ============================================
      // Save before push notifications so users can see the announcement immediately on click
      console.log(`\n✅ All WhatsApp batches completed!`);
      console.log(`   Total sent: ${totalSuccessCount}/${uniquePhoneList.length}`);
      console.log(`   Total failed: ${totalFailureCount}`);

      setCurrentRecipient({ name: 'Saving announcement...', phone: '' });
      await saveAnnouncementRecord(totalSuccessCount, totalFailureCount, uploadedMediaIds);

      // ============================================
      // STEP 6: SEND PUSH NOTIFICATIONS (if method is 'both') — BULK
      // ============================================
      if (shouldSendNotification) {
        console.log('🔔 Sending bulk push notifications to selected students...');
        setCurrentRecipient({ name: `Sending push to ${selectedStudents.length} students...`, phone: '' });
        try {
          await authFetch(`${API_BASE_URL}/api/push-token/bulk-send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: noticeTitle,
              body: noticeBody,
              studentRnos: selectedStudents,
            }),
          });
          console.log('✅ Bulk push notifications sent');
        } catch (err) {
          console.error('❌ Bulk push notification failed:', err);
        }
      }

      // All steps processed
      setProgressStatus('completed');
      setCurrentRecipient(null);
      
      // Reset form only if at least some messages were sent successfully
      if (totalSuccessCount > 0) {
        setTimeout(() => {
          // Reset template fields
          setNoticeDate(new Date().toISOString().split('T')[0]); // Reset to today
          setNoticeTitle('');
          setNoticeBody('');
          setAttachments([]);
          setSelectedStudents([]);
          
          // Cleanup attachment previews
          attachments.forEach(att => {
            if (att.preview) {
              URL.revokeObjectURL(att.preview);
            }
          });
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error in send process:', error);
      setProgressStatus('completed');
      showNotification('An error occurred during sending', 'error');
    } finally {
      setSending(false);
    }
  };



  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const closeProgressModal = () => {
    setShowProgressModal(false);
    // Reset progress states
    setTotalMessages(0);
    setSentMessages(0);
    setFailedMessages(0);
    setSkippedMessages(0);
    setSendResults({ successful: [], failed: [], skipped: [] });
    setCurrentRecipient(null);
    setProgressStatus('sending');
  };

  // ===== DEMAND SLIP: Fetch and compute fee data =====
  const fetchDemandSlipData = useCallback(async () => {
    try {
      setLoadingDemandSlip(true);
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 1-indexed
      const isExamMonth = EXAM_FEE_MONTHS.includes(currentMonth);

      // 1. Fetch classes with fees
      const { data: classes, error: classError } = await supabase
        .from('classes')
        .select('class_name, fee, transport_fee, exam_fee');
      if (classError) throw classError;

      const classFeeMap = {};
      const classTransportMap = {};
      const classExamMap = {};
      (classes || []).forEach(cls => {
        classFeeMap[cls.class_name] = parseFloat(cls.fee) || 0;
        classTransportMap[cls.class_name] = parseFloat(cls.transport_fee) || 0;
        classExamMap[cls.class_name] = parseFloat(cls.exam_fee) || 0;
      });

      // 2. Fetch active students
      const { data: studentsData, error: studentError } = await supabase
        .from('students')
        .select('Rno, Name, Class, Transport, "Transport Fee", Active')
        .eq('Active', true);
      if (studentError) throw studentError;

      // 3. Fetch private details (FeeDues, phone, discount)
      const rnos = (studentsData || []).map(s => s.Rno);
      const { data: privateData, error: privateError } = await supabase
        .from('students_private_details')
        .select('Rno, FeeDues, "Default Discount", Mobile1, Mobile2')
        .in('Rno', rnos);
      if (privateError) throw privateError;

      const privateMap = {};
      (privateData || []).forEach(p => { privateMap[p.Rno] = p; });

      // 4. Check who already paid this month
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const { data: monthPayments, error: payError } = await supabase
        .from('payment_transactions')
        .select('student_rno, current_month_fee, examination_fee, transport_fee')
        .in('student_rno', rnos)
        .gte('created_at', monthStart)
        .lte('created_at', monthEnd);
      if (payError) throw payError;

      const paidMonthlySet = new Set();
      const paidExamSet = new Set();
      const paidTransportSet = new Set();
      (monthPayments || []).forEach(txn => {
        if ((txn.current_month_fee || 0) > 0) paidMonthlySet.add(txn.student_rno);
        if ((txn.examination_fee || 0) > 0) paidExamSet.add(txn.student_rno);
        if ((txn.transport_fee || 0) > 0) paidTransportSet.add(txn.student_rno);
      });

      // 5. Compute demand slip data for each student
      const computed = (studentsData || []).map(student => {
        const priv = privateMap[student.Rno] || {};
        const feeDues = parseFloat(priv.FeeDues) || 0;
        // Skip default discount if tuition already paid this month
        const discount = paidMonthlySet.has(student.Rno) ? 0 : (parseFloat(priv['Default Discount']) || 0);

        // Arrears: FeeDues < 0 means student owes money
        const arrears = feeDues < 0 ? Math.abs(feeDues) : 0;
        const advance = feeDues > 0 ? feeDues : 0;

        // Tuition fee (0 if already paid this month)
        const tuitionFee = paidMonthlySet.has(student.Rno) ? 0 : (classFeeMap[student.Class] || 0);

        // Transport fee
        let transportFee = 0;
        if (student.Transport === true && !paidTransportSet.has(student.Rno)) {
          const studentTransportFee = parseFloat(student['Transport Fee']) || 0;
          const classTransportFee = classTransportMap[student.Class] || 0;
          transportFee = studentTransportFee > 0 ? studentTransportFee : classTransportFee;
        }

        // Exam fee (only in exam months, 0 if already paid)
        let examFee = 0;
        if (isExamMonth && !paidExamSet.has(student.Rno)) {
          examFee = classExamMap[student.Class] || 0;
        }

        // Total: arrears + fees - advance - discount
        const grossTotal = arrears + tuitionFee + transportFee + examFee;
        const total = Math.max(0, grossTotal - advance - discount);

        return {
          Rno: student.Rno,
          Name: student.Name,
          Class: student.Class,
          Mobile1: priv.Mobile1 || null,
          Mobile2: priv.Mobile2 || null,
          arrears,
          tuitionFee,
          transportFee,
          examFee,
          discount,
          advance,
          total,
        };
      });

      setDemandSlipData(computed);
    } catch (error) {
      console.error('Error fetching demand slip data:', error);
      showNotification('Failed to load demand slip data', 'error');
    } finally {
      setLoadingDemandSlip(false);
    }
  }, [showNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch demand slip data when mode switches
  useEffect(() => {
    if (pageMode === 'demand_slip' && demandSlipData.length === 0) {
      fetchDemandSlipData();
    }
  }, [pageMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter demand slip students by class and search
  const filteredDemandSlipData = demandSlipData.filter(student => {
    if (classFilter !== 'all' && student.Class !== classFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        (student.Name?.toLowerCase() || '').includes(search) ||
        (student.Rno?.toString() || '').includes(search) ||
        (student.Class?.toLowerCase() || '').includes(search)
      );
    }
    return true;
  });

  const handleDemandSlipSort = (field) => {
    setDemandSlipSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedDemandSlipData = [...filteredDemandSlipData].sort((a, b) => {
    const { field, direction } = demandSlipSort;
    if (!field) return 0;
    const mul = direction === 'asc' ? 1 : -1;
    const valA = a[field];
    const valB = b[field];
    if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * mul;
    return String(valA || '').localeCompare(String(valB || '')) * mul;
  });

  // ===== DEMAND SLIP: Preview then send =====
  const handlePreviewDemandSlips = async () => {
    if (selectedStudents.length === 0) {
      showNotification('Please select at least one student', 'error');
      return;
    }
    if (!demandSlipDate.trim() || !demandSlipMonth.trim()) {
      showNotification('Please fill date and month', 'error');
      return;
    }

    // Check which students already received a demand slip this month
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
    const selectedPhones = demandSlipData
      .filter(s => selectedStudents.includes(s.Rno))
      .flatMap(s => [s.Mobile1, s.Mobile2])
      .filter(Boolean);

    if (selectedPhones.length > 0) {
      try {
        const { data: existingMsgs } = await supabase
          .from('whatsapp_messages')
          .select('recipient_phone, message_text')
          .in('recipient_phone', selectedPhones)
          .gte('sent_at', monthStart);

        // Filter in JS for reliability (avoid ilike pattern issues)
        const demandSlipPhones = new Set(
          (existingMsgs || [])
            .filter(m => m.message_text && m.message_text.startsWith('Demand Slip -'))
            .map(m => m.recipient_phone)
        );

        // Mark students as already sent if any of their phones have a demand slip this month
        setDemandSlipData(prev => prev.map(s => ({
          ...s,
          _alreadySent: demandSlipPhones.has(s.Mobile1) || demandSlipPhones.has(s.Mobile2),
        })));
      } catch (err) {
        console.warn('Could not check already-sent status:', err);
      }
    }

    setShowDemandSlipPreview(true);
  };

  // ===== DEMAND SLIP: Send handler =====
  const handleSendDemandSlips = async () => {
    setShowDemandSlipPreview(false);

    const recipientsData = demandSlipData
      .filter(s => selectedStudents.includes(s.Rno) && s.total > 0)
      .map(s => ({
        rno: s.Rno,
        name: s.Name,
        className: s.Class,
        phone: s.Mobile1 ? `91${s.Mobile1}` : null,
        alternatePhone: s.Mobile2 ? `91${s.Mobile2}` : null,
        arrears: s.arrears,
        tuitionFee: s.tuitionFee,
        conveyance: s.transportFee,
        examFee: s.examFee,
        total: s.total,
      }))
      .filter(s => s.phone);

    if (recipientsData.length === 0) {
      showNotification('Selected students have no phone numbers', 'error');
      return;
    }

    // Deduplicate by phone
    const uniquePhones = new Set();
    recipientsData.forEach(r => {
      if (r.phone) uniquePhones.add(r.phone);
      if (r.alternatePhone) uniquePhones.add(r.alternatePhone);
    });

    setTotalMessages(uniquePhones.size);
    setSentMessages(0);
    setFailedMessages(0);
    setSkippedMessages(0);
    setSendResults({ successful: [], failed: [], skipped: [] });
    setProgressStatus('sending');
    setShowProgressModal(true);
    setSending(true);

    try {
      // Send in batches of 3 (each student needs JPEG generation + WhatsApp upload — keep under Render's 30s timeout)
      const BATCH_SIZE = 3;
      const totalBatches = Math.ceil(recipientsData.length / BATCH_SIZE);
      let totalSuccessCount = 0;
      let totalFailureCount = 0;
      let totalSkippedCount = 0;
      const allSuccessResults = [];
      const allFailedResults = [];
      const allSkippedResults = [];

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const start = batchIndex * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, recipientsData.length);
        const batch = recipientsData.slice(start, end);
        const batchNum = batchIndex + 1;

        setCurrentRecipient({
          name: `Batch ${batchNum}/${totalBatches}`,
          phone: `${batch.length} students`
        });

        try {
          const response = await authFetch(`${API_BASE_URL}/api/whatsapp/send-demand-slip`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: demandSlipDate,
              month: demandSlipMonth,
              students: batch,
              forceSend: forceSend,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.details) {
              totalSuccessCount += result.details.successCount || 0;
              totalFailureCount += result.details.failedCount || 0;
              totalSkippedCount += result.details.skippedCount || 0;
              if (result.details.results?.successful) allSuccessResults.push(...result.details.results.successful);
              if (result.details.results?.failed) allFailedResults.push(...result.details.results.failed);
              if (result.details.results?.skipped) allSkippedResults.push(...result.details.results.skipped);
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Request failed');
          }
        } catch (error) {
          console.error(`Batch ${batchNum} failed:`, error);
          batch.forEach(s => {
            totalFailureCount++;
            allFailedResults.push({ recipient: s.name, error: error.message });
          });
        }

        setSentMessages(totalSuccessCount);
        setFailedMessages(totalFailureCount);
        setSkippedMessages(totalSkippedCount);
        setSendResults({ successful: allSuccessResults, failed: allFailedResults, skipped: allSkippedResults });

        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setProgressStatus('completed');
      setCurrentRecipient(null);

      if (totalSuccessCount > 0) {
        setTimeout(() => {
          setSelectedStudents([]);
        }, 1000);
      }
    } catch (error) {
      console.error('Error sending demand slips:', error);
      setProgressStatus('completed');
      showNotification('An error occurred during sending', 'error');
    } finally {
      setSending(false);
      setForceSend(false);
    }
  };

  const channelCount = sendMethod === 'both' ? 2 : 1;

  if (loading) {
    return (
      <div className="bn-container">
        <div className="bn-page-header">
          <h1>Bulk Notification Dashboard</h1>
        </div>
        <div className="bn-loading">
          <div className="bn-spinner" />
          <p>Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bn-container">
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      <SendProgressModal
        show={showProgressModal}
        onClose={closeProgressModal}
        totalMessages={totalMessages}
        sentMessages={sentMessages}
        failedMessages={failedMessages}
        skippedMessages={skippedMessages}
        currentRecipient={currentRecipient}
        status={progressStatus}
        results={sendResults}
      />
      <WhatsAppPreviewModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onConfirm={handleConfirmSend}
        noticeDate={noticeDate}
        noticeTitle={noticeTitle}
        noticeBody={noticeBody}
        attachment={attachments.length > 0 ? attachments[0] : null}
        recipientCount={selectedStudents.length}
      />

      {/* Demand Slip Confirmation Modal */}
      {showDemandSlipPreview && (() => {
        const allSelected = demandSlipData.filter(s => selectedStudents.includes(s.Rno));
        const zeroDue = allSelected.filter(s => s.total === 0);
        const previewStudents = allSelected.filter(s => s.total > 0);
        const withPhone = previewStudents.filter(s => s.Mobile1 || s.Mobile2);
        const withoutPhone = previewStudents.filter(s => !s.Mobile1 && !s.Mobile2);
        const uniquePhones = new Set();
        previewStudents.forEach(s => {
          if (s.Mobile1) uniquePhones.add(s.Mobile1);
          if (s.Mobile2) uniquePhones.add(s.Mobile2);
        });

        const alreadySent = previewStudents.filter(s => s._alreadySent);
        const willSend = forceSend ? withPhone : withPhone.filter(s => !s._alreadySent);

        return (
          <div className="bn-modal-overlay" onClick={() => { setShowDemandSlipPreview(false); setForceSend(false); }}>
            <div className="bn-modal-content bn-ds-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bn-modal-header">
                <h3><FaFileInvoiceDollar style={{ marginRight: 8 }} /> Confirm Demand Slip Sending</h3>
                <button className="bn-modal-close" onClick={() => { setShowDemandSlipPreview(false); setForceSend(false); }}><FaTimes /></button>
              </div>
              <div className="bn-modal-body">
                <div className="bn-ds-preview-grid">
                  <div className="bn-ds-preview-item">
                    <span className="bn-ds-preview-label">Date on Slip</span>
                    <span className="bn-ds-preview-value">{demandSlipDate}</span>
                  </div>
                  <div className="bn-ds-preview-item">
                    <span className="bn-ds-preview-label">Month</span>
                    <span className="bn-ds-preview-value">{demandSlipMonth}</span>
                  </div>
                  <div className="bn-ds-preview-item">
                    <span className="bn-ds-preview-label">Selected Students</span>
                    <span className="bn-ds-preview-value">{previewStudents.length}</span>
                  </div>
                  {zeroDue.length > 0 && (
                    <div className="bn-ds-preview-item">
                      <span className="bn-ds-preview-label">Skipped (no dues)</span>
                      <span className="bn-ds-preview-value" style={{ color: '#f59e0b' }}>{zeroDue.length}</span>
                    </div>
                  )}
                  <div className="bn-ds-preview-item">
                    <span className="bn-ds-preview-label">Recipients (unique phones)</span>
                    <span className="bn-ds-preview-value">{uniquePhones.size}</span>
                  </div>
                </div>

                {alreadySent.length > 0 && (
                  <div className="bn-ds-preview-info">
                    <strong>📋 {alreadySent.length} student(s) already received a demand slip this month.</strong>
                    {!forceSend ? (
                      <span> They will be <strong>skipped</strong>. Check "Force Send" below to resend.</span>
                    ) : (
                      <span style={{ color: '#dc2626' }}> <strong>Force Send is ON</strong> — they will receive it again.</span>
                    )}
                  </div>
                )}

                {withoutPhone.length > 0 && (
                  <div className="bn-ds-preview-warning">
                    <strong>⚠️ {withoutPhone.length} student(s) have no phone number and will be skipped:</strong>
                    <span>{withoutPhone.map(s => s.Name).join(', ')}</span>
                  </div>
                )}

                <label className="bn-ds-force-checkbox">
                  <input
                    type="checkbox"
                    checked={forceSend}
                    onChange={(e) => setForceSend(e.target.checked)}
                  />
                  <span>Force Send — resend to students who already received a demand slip this month</span>
                </label>

                <p className="bn-ds-preview-note">
                  Each student will receive a personalized demand slip.
                </p>
              </div>
              <div className="bn-modal-footer">
                <button className="bn-btn-cancel" onClick={() => { setShowDemandSlipPreview(false); setForceSend(false); }}>Cancel</button>
                <button className="bn-btn-confirm" onClick={handleSendDemandSlips}>
                  <FaPaperPlane style={{ marginRight: 6 }} /> Send to {willSend.length} Student{willSend.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Page Header + Mode Selector */}
      <div className="bn-page-header">
        <h1>Bulk Notification Dashboard</h1>
        <div className="bn-mode-selector">
          <select
            className="bn-mode-dropdown"
            value={pageMode}
            onChange={(e) => { setPageMode(e.target.value); setSelectedStudents([]); setClassFilter('all'); setSearchTerm(''); }}
            disabled={sending}
          >
            <option value="notice">Bulk Notice</option>
            <option value="demand_slip">Demand Slip</option>
          </select>
        </div>
      </div>

      {/* ===== DEMAND SLIP MODE ===== */}
      {pageMode === 'demand_slip' ? (
        <div className="bn-demand-slip-container">
          {/* Demand Slip Controls */}
          <div className="bn-card bn-ds-controls">
            <div className="bn-card-header">
              <h2><FaFileInvoiceDollar style={{ marginRight: 8 }} /> Demand Slip Settings</h2>
              <button className="bn-link-btn" onClick={fetchDemandSlipData} disabled={sending || loadingDemandSlip}>
                <FaSync className={loadingDemandSlip ? 'bn-spinning' : ''} /> Refresh Data
              </button>
            </div>
            <div className="bn-card-body">
              <div className="bn-ds-fields">
                <div className="bn-field">
                  <label className="bn-label">Date (on slip)</label>
                  <input
                    type="text"
                    className="bn-input"
                    value={demandSlipDate}
                    onChange={(e) => setDemandSlipDate(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    disabled={sending}
                  />
                </div>
                <div className="bn-field">
                  <label className="bn-label">Month (on slip)</label>
                  <input
                    type="text"
                    className="bn-input"
                    value={demandSlipMonth}
                    onChange={(e) => setDemandSlipMonth(e.target.value)}
                    placeholder="e.g. July"
                    disabled={sending}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Demand Slip Student Table */}
          <div className="bn-card">
            <div className="bn-card-header">
              <h2>Student Fee Details</h2>
              <div className="bn-selection-status">
                <span className="bn-selected-badge">
                  <FaCheckCircle /> {selectedStudents.length} selected
                </span>
                <button className="bn-link-btn" onClick={() => { const all = filteredDemandSlipData.map(s => s.Rno); setSelectedStudents(all); }} disabled={sending}>
                  Select All ({filteredDemandSlipData.length})
                </button>
                <button className="bn-link-btn bn-link-clear" onClick={clearSelection} disabled={sending}>
                  Clear
                </button>
              </div>
            </div>
            <div className="bn-card-body">
              {/* Filters */}
              <div className="bn-filters">
                <div className="bn-filter-item">
                  <label className="bn-label">Class</label>
                  <select className="bn-input bn-select" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} disabled={sending}>
                    <option value="all">All Classes</option>
                    {availableClasses.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
                <div className="bn-filter-item bn-filter-search">
                  <label className="bn-label">Search</label>
                  <input
                    type="text"
                    className="bn-input"
                    placeholder="Name or Roll No..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={sending}
                  />
                </div>
              </div>

              {/* Demand Slip Table */}
              <div className="bn-table-wrapper bn-ds-table-wrapper">
                {loadingDemandSlip ? (
                  <div className="bn-empty"><div className="bn-spinner" style={{ margin: '0 auto 10px' }} /><p>Computing fee data...</p></div>
                ) : filteredDemandSlipData.length === 0 ? (
                  <div className="bn-empty">No students found</div>
                ) : (
                  <table className="bn-table bn-ds-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        {[
                          { label: 'Rno', field: 'Rno' },
                          { label: 'Name', field: 'Name' },
                          { label: 'Class', field: 'Class' },
                          { label: 'Arrears', field: 'arrears', cls: 'bn-ds-num' },
                          { label: 'Tuition', field: 'tuitionFee', cls: 'bn-ds-num' },
                          { label: 'Conveyance', field: 'transportFee', cls: 'bn-ds-num' },
                          { label: 'Exam Fee', field: 'examFee', cls: 'bn-ds-num' },
                          { label: 'Total', field: 'total', cls: 'bn-ds-num bn-ds-total-col' },
                        ].map(col => (
                          <th
                            key={col.field}
                            className={`${col.cls || ''} bn-sortable-th`}
                            onClick={() => handleDemandSlipSort(col.field)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            {col.label} {demandSlipSort.field === col.field ? (demandSlipSort.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDemandSlipData.map((student) => (
                        <tr
                          key={student.Rno}
                          className={selectedStudents.includes(student.Rno) ? 'bn-row-selected' : ''}
                          onClick={() => toggleStudentSelection(student.Rno)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="bn-checkbox"
                              checked={selectedStudents.includes(student.Rno)}
                              onChange={() => toggleStudentSelection(student.Rno)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={sending}
                            />
                          </td>
                          <td>{student.Rno}</td>
                          <td className="bn-name-cell">{student.Name}</td>
                          <td><span className="bn-class-badge">{student.Class || 'N/A'}</span></td>
                          <td className="bn-ds-num">
                            {student.arrears > 0 ? <span className="bn-ds-red">₹{student.arrears.toLocaleString('en-IN')}</span> : student.advance > 0 ? <span className="bn-ds-green">₹{student.advance.toLocaleString('en-IN')}</span> : <span className="bn-ds-zero">0</span>}
                          </td>
                          <td className="bn-ds-num">{student.tuitionFee > 0 ? `₹${student.tuitionFee.toLocaleString('en-IN')}` : <span className="bn-ds-zero">0</span>}</td>
                          <td className="bn-ds-num">{student.transportFee > 0 ? `₹${student.transportFee.toLocaleString('en-IN')}` : <span className="bn-ds-zero">0</span>}</td>
                          <td className="bn-ds-num">{student.examFee > 0 ? `₹${student.examFee.toLocaleString('en-IN')}` : <span className="bn-ds-zero">0</span>}</td>
                          <td className="bn-ds-num bn-ds-total-col"><strong>₹{student.total.toLocaleString('en-IN')}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
      /* ===== NOTICE MODE (existing UI) ===== */
      <div className="bn-grid">
        {/* ===== LEFT: Notice Composer ===== */}
        <div className="bn-card">
          <div className="bn-card-header">
            <h2>Notice Composer</h2>
          </div>
          <div className="bn-card-body">
            {/* Date */}
            <div className="bn-field">
              <label className="bn-label">Date</label>
              <input
                type="date"
                className="bn-input"
                value={noticeDate}
                onChange={(e) => setNoticeDate(e.target.value)}
                disabled={sending}
              />
            </div>

            {/* Title */}
            <div className="bn-field">
              <label className="bn-label">Notice Title</label>
              <input
                type="text"
                className="bn-input"
                value={noticeTitle}
                onChange={(e) => setNoticeTitle(e.target.value)}
                placeholder="e.g., School Closure, Important Announcement"
                disabled={sending}
              />
            </div>

            {/* Delivery Channels */}
            <div className="bn-field">
              <label className="bn-label">Delivery Channels</label>
              <div className="bn-channels">
                {SEND_METHODS.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    className={`bn-channel-btn ${sendMethod === m.value ? 'active' : ''}`}
                    onClick={() => setSendMethod(m.value)}
                    disabled={sending}
                  >
                    {m.value === 'both' && <><FaWhatsapp /><span>+</span><FaBell /></>}
                    {m.value === 'whatsapp_only' && <FaWhatsapp />}
                    {m.value === 'notification_only' && <FaBell />}
                    <span className="bn-channel-label">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="bn-field">
              <label className="bn-label">Main Content</label>
              <textarea
                className="bn-textarea"
                value={noticeBody}
                onChange={(e) => setNoticeBody(e.target.value)}
                placeholder="Enter the main content of the notice..."
                rows="5"
                disabled={sending}
              />
            </div>

            {/* Attachments */}
            <div className="bn-field">
              <div className="bn-attachment-header">
                <label className="bn-label">Attachments</label>
                <span className="bn-tag">IMG</span>
              </div>
              <div
                className="bn-dropzone"
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <FaUpload className="bn-dropzone-icon" />
                <span>Add Media</span>
              </div>

              {attachments.length > 0 && (
                <div className="bn-attachment-list">
                  {attachments.map((attachment, index) => (
                    <div key={index} className="bn-attachment-item">
                      {attachment.preview ? (
                        <div className="bn-thumb-wrapper">
                          <img
                            src={attachment.preview}
                            alt={attachment.name}
                            className="bn-attachment-thumb"
                            onClick={(e) => { e.stopPropagation(); setPreviewImage(attachment.preview); }}
                            style={{ cursor: 'zoom-in' }}
                          />
                          {attachment.isGallery && <span className="bn-gallery-badge">Gallery</span>}
                        </div>
                      ) : (
                        getFileIcon(attachment.type)
                      )}
                      <div className="bn-attachment-info">
                        <span className="bn-attachment-name">{attachment.name}</span>
                        <span className="bn-attachment-size">
                          {attachment.isGallery ? 'From Gallery' : formatFileSize(attachment.size)}
                        </span>
                      </div>
                      <button className="bn-remove-btn" onClick={() => removeAttachment(index)} disabled={sending}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== RIGHT: Audience Selection ===== */}
        <div className="bn-card">
          <div className="bn-card-header">
            <h2>Audience Selection</h2>
            <div className="bn-selection-status">
              <span className="bn-selected-badge">
                <FaCheckCircle /> {selectedStudents.length} selected
              </span>
              <button className="bn-link-btn" onClick={selectAllFiltered} disabled={sending}>
                Select All ({filteredStudents.length})
              </button>
              <button className="bn-link-btn bn-link-clear" onClick={clearSelection} disabled={sending}>
                Clear
              </button>
            </div>
          </div>
          <div className="bn-card-body">
            {/* Filters */}
            <div className="bn-filters">
              <div className="bn-filter-item">
                <label className="bn-label">Class</label>
                <select className="bn-input bn-select" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} disabled={sending}>
                  <option value="all">All Classes</option>
                  {availableClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
              <div className="bn-filter-item bn-filter-search">
                <label className="bn-label">Student Name / Roll No</label>
                <input
                  type="text"
                  className="bn-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={sending}
                />
              </div>
            </div>

            {/* Students Table */}
            <div className="bn-table-wrapper">
              {filteredStudents.length === 0 ? (
                <div className="bn-empty">No students found matching your filters</div>
              ) : (
                <table className="bn-table">
                  <thead>
                    <tr>
                      <th style={{width: 40}}></th>
                      <th>Roll No</th>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Contact 1</th>
                      <th>Contact 2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr
                        key={student.Rno}
                        className={selectedStudents.includes(student.Rno) ? 'bn-row-selected' : ''}
                        onClick={() => toggleStudentSelection(student.Rno)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            className="bn-checkbox"
                            checked={selectedStudents.includes(student.Rno)}
                            onChange={() => toggleStudentSelection(student.Rno)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={sending}
                          />
                        </td>
                        <td>{student.Rno}</td>
                        <td className="bn-name-cell">{student.Name}</td>
                        <td><span className="bn-class-badge">{student.Class || 'N/A'}</span></td>
                        <td>{student.Mobile1 || <span className="bn-na">N/A</span>}</td>
                        <td>{student.Mobile2 || <span className="bn-na">N/A</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="bn-image-overlay" onClick={() => setPreviewImage(null)}>
          <div className="bn-image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="bn-image-close" onClick={() => setPreviewImage(null)}>
              <FaTimes />
            </button>
            <img src={previewImage} alt="Preview" className="bn-image-full" />
          </div>
        </div>
      )}

      {/* ===== Bottom: Send Action ===== */}
      <div className="bn-send-bar">
        {pageMode === 'demand_slip' ? (
          <>
            <div className="bn-send-info">
              {(() => {
                const zeroTotal = demandSlipData.filter(s => selectedStudents.includes(s.Rno) && s.total === 0).length;
                const toSend = selectedStudents.length - zeroTotal;
                return <>
                  Sending demand slips to <strong>{toSend}</strong> student{toSend !== 1 ? 's' : ''} via <strong>WhatsApp</strong>
                  {zeroTotal > 0 && <span style={{ color: '#f59e0b', marginLeft: 8 }}>({zeroTotal} skipped &rarr; no dues)</span>}
                </>;
              })()}
            </div>
            <button
              className="bn-send-btn"
              onClick={handlePreviewDemandSlips}
              disabled={selectedStudents.length === 0 || sending || !demandSlipDate.trim() || !demandSlipMonth.trim()}
            >
              {sending ? (
                <><div className="bn-btn-spinner" /> Sending...</>
              ) : (
                <><FaFileInvoiceDollar /> Send Demand Slips</>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="bn-send-info">
              Sending to <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? 's' : ''} across <strong>{channelCount}</strong> channel{channelCount !== 1 ? 's' : ''}
            </div>
            <button
              className="bn-send-btn"
              onClick={handleSendNotice}
              disabled={selectedStudents.length === 0 || sending || !noticeDate.trim() || !noticeTitle.trim() || (attachments.length === 0 && !noticeBody.trim())}
            >
              {sending ? (
                <><div className="bn-btn-spinner" /> Sending...</>
              ) : (
                <><FaPaperPlane /> Send Notice</>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkNotice;
